import { getActiveExploitantId, clearActiveExploitantId } from './activeExploitant';
import { HOME_APPLIED_KEY } from './moduleAccess';

const TOKEN_KEY = 'jlfc_token';
const ROOT_TENANT_HEADER = 'X-Exploitant-Id';

/** Routes publiques : un 401 ne doit pas déclencher de redirection login. */
const PUBLIC_AUTH_PATHS = ['/api/login', '/api/forgot-password', '/api/reset-password', '/api/ping'];

let sessionRedirecting = false;

function appendRootTenantHeader(headers = {}) {
  const next = { ...headers };
  const activeExploitantId = getActiveExploitantId();
  if (activeExploitantId) {
    next[ROOT_TENANT_HEADER] = activeExploitantId;
  }
  return next;
}

export function getAuthToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearAuthSession() {
  localStorage.removeItem(TOKEN_KEY);
  clearActiveExploitantId();
  sessionStorage.removeItem(HOME_APPLIED_KEY);
}

function isPublicAuthPath(url) {
  const path = String(url).split('?')[0];
  return PUBLIC_AUTH_PATHS.some((p) => path === p || path.endsWith(p));
}

/**
 * Session expirée ou token invalide : purge le token et retour à la page de connexion.
 */
export function handleSessionExpired() {
  if (sessionRedirecting) return;
  sessionRedirecting = true;
  clearAuthSession();
  const { pathname, search } = window.location;
  if (pathname === '/reset-password') {
    window.location.reload();
    return;
  }
  const returnTo = pathname + search;
  const loginUrl =
    returnTo && returnTo !== '/'
      ? `/?session=expired&returnTo=${encodeURIComponent(returnTo)}`
      : '/?session=expired';
  window.location.replace(loginUrl);
}

async function handleAuthResponse(res, url, hadToken) {
  if (res.status === 401 && hadToken && !isPublicAuthPath(url)) {
    handleSessionExpired();
    return res;
  }
  if (res.status === 403 && hadToken && !isPublicAuthPath(url)) {
    try {
      const clone = res.clone();
      const data = await clone.json();
      if (data?.code === 'LICENCE_EXPIRED') {
        handleSessionExpired();
      }
    } catch {
      // ignore parse errors
    }
  }
  return res;
}

export async function authFetch(url, options = {}) {
  const token = getAuthToken();
  const hadToken = Boolean(token);
  const headers = appendRootTenantHeader({ ...options.headers });
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(url, { ...options, headers });
  return handleAuthResponse(res, url, hadToken);
}

/** Upload multipart (ne pas définir Content-Type) */
export async function parseJsonResponse(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    if (res.status === 429 || /429\s+Too Many Requests/i.test(text)) {
      return {
        success: false,
        message: 'Trop de requêtes — patientez quelques secondes puis réessayez.',
      };
    }
    if (
      text.includes('Cannot GET') ||
      text.includes('<!DOCTYPE') ||
      text.includes('<html')
    ) {
      return {
        success: false,
        message:
          res.status >= 500
            ? 'Erreur serveur temporaire. Réessayez dans un instant.'
            : 'Réponse serveur invalide. Réessayez ou contactez le support.',
      };
    }
    return { success: false, message: text.slice(0, 300) || `Erreur HTTP ${res.status}` };
  }
}

export async function authUpload(url, formData, method = 'POST') {
  const token = getAuthToken();
  const hadToken = Boolean(token);
  const headers = appendRootTenantHeader({});
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(url, { method, headers, body: formData });
  return handleAuthResponse(res, url, hadToken);
}
